import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight, AlertTriangle } from "lucide-react";
import { API, scrollToTop, setCanonical } from "../lib/config";
import { useSettings, getAffiliate } from "../lib/useSettings";
import { Nav, Footer, HeroBanner, CookieConsent, QuizTeaser } from "../components/layout";
import { ChatBot } from "../components/chat";
import { MoneyStrip, AdSensePlaceholder, AdSenseOnly } from "../components/ads";

export default function StatePage() {
  const { stateSlug } = useParams();
  const [state, setState] = useState(null);
  const settings = useSettings();

  useEffect(() => {
    axios.get(`${API}/state/${stateSlug}`).then(r => {
      const s = r.data;
      setState(s);
      setCanonical(`/states/${stateSlug}`);
      document.title = `Auto Insurance in ${s.name} | State Requirements & City Guides | QuotesByCity`;

      // Meta description
      const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
      meta.name = "description";
      meta.content = `Auto insurance in ${s.name} requires a minimum of ${s.min_auto} liability coverage. Average premium is $${s.avg_auto_premium?.toLocaleString()}/year. Free city-by-city guides for all ${s.city_count} cities.`;
      if (!meta.parentNode) document.head.appendChild(meta);

      // Schemas
      const schemas = [
        ["state-service", { "@context": "https://schema.org", "@type": "Service", "name": `Auto Insurance Education for ${s.name}`, "description": `Free auto insurance education for ${s.name} drivers. State minimum requirements: ${s.min_auto}. Average premium: $${s.avg_auto_premium?.toLocaleString()}/year. City-specific guides for all ${s.city_count} cities.`, "provider": { "@type": "Organization", "name": "QuotesByCity", "url": "https://www.quotesbycity.com" }, "areaServed": { "@type": "State", "name": s.name }, "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" } }],
        ["state-faq", { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [
          { "@type": "Question", "name": `What is the minimum auto insurance required in ${s.name}?`, "acceptedAnswer": { "@type": "Answer", "text": `${s.name} requires a minimum of ${s.min_auto} liability coverage. This is the legal minimum — most experts recommend higher limits to protect your assets.` }},
          { "@type": "Question", "name": `What is the average auto insurance cost in ${s.name}?`, "acceptedAnswer": { "@type": "Answer", "text": `The average annual auto insurance premium in ${s.name} is approximately $${s.avg_auto_premium?.toLocaleString()}. Rates vary by city, driving record, vehicle, and coverage level.` }},
          { "@type": "Question", "name": `How many cities in ${s.name} does QuotesByCity cover?`, "acceptedAnswer": { "@type": "Answer", "text": `QuotesByCity covers ${s.city_count} cities in ${s.name}, each with a specific auto insurance guide including local risk factors and coverage recommendations.` }},
          { "@type": "Question", "name": `What are the main risk factors affecting auto insurance rates in ${s.name}?`, "acceptedAnswer": { "@type": "Answer", "text": `${s.name} drivers face specific risk factors including ${s.risk_factors?.join(", ") || "weather, traffic, and local accident rates"} that can affect auto insurance premiums.` }},
        ]}],
        ["state-bread", { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.quotesbycity.com" },
          { "@type": "ListItem", "position": 2, "name": "States", "item": "https://www.quotesbycity.com/states" },
          { "@type": "ListItem", "position": 3, "name": `${s.name} Auto Insurance`, "item": `https://www.quotesbycity.com/states/${stateSlug}` },
        ]}],
        ["state-speak", { "@context": "https://schema.org", "@type": "WebPage", "name": `Auto Insurance in ${s.name}`, "description": `Auto insurance in ${s.name} requires a minimum of ${s.min_auto} liability coverage with an average premium of $${s.avg_auto_premium?.toLocaleString()}/year. Free guides for all ${s.city_count} cities.`, "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro", "[data-testid='state-stats']"] }, "url": `https://www.quotesbycity.com/states/${stateSlug}` }],
      ];
      schemas.forEach(([id, schema]) => {
        let el = document.getElementById(`schema-${id}`);
        if (!el) { el = document.createElement("script"); el.id = `schema-${id}`; el.type = "application/ld+json"; document.head.appendChild(el); }
        el.textContent = JSON.stringify(schema);
      });
    }).catch(() => {});
    return () => { ["state-service", "state-faq", "state-bread", "state-speak"].forEach(id => { const el = document.getElementById(`schema-${id}`); if (el) el.remove(); }); };
  }, [stateSlug]);

  if (!state) return <div className="min-h-screen bg-[#FAFAFA]"><Nav /><div className="max-w-4xl mx-auto px-4 py-20 text-center text-slate-400">Loading...</div></div>;

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <HeroBanner title={`Auto Insurance in ${state.name}`} subtitle={`State requirements, average rates, and city guides for ${state.name} drivers`} badge={`${state.name} (${state.code})`} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* GEO: Definition-lead sentence for AI engine citation */}
        <p className="speakable-intro text-sm text-sky-700 font-medium mb-6">
          Auto insurance in {state.name} requires a minimum of {state.min_auto} liability coverage, with an average annual premium of ${state.avg_auto_premium?.toLocaleString()} — QuotesByCity provides free city-specific guides for all {state.city_count} cities in {state.name}.
        </p>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* MAIN CONTENT */}
          <div className="flex-1 min-w-0 space-y-8">

            {/* STATE STATS */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="state-stats">
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <p className="text-xs text-slate-500 mb-1">Minimum Liability</p>
                <p className="text-lg font-bold text-slate-900">{state.min_auto}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <p className="text-xs text-slate-500 mb-1">Average Premium</p>
                <p className="text-lg font-bold text-slate-900">${state.avg_auto_premium?.toLocaleString()}/yr</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <p className="text-xs text-slate-500 mb-1">Cities Covered</p>
                <p className="text-lg font-bold text-slate-900">{state.city_count}</p>
              </div>
            </div>

            {/* WARNING */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">
                <strong>{state.name}'s minimum liability of {state.min_auto}</strong> is the legal minimum, not a recommendation. A single accident can easily exceed these limits. Consider higher coverage to protect yourself.
              </p>
            </div>

            {/* RISK FACTORS */}
            {state.risk_factors && state.risk_factors.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Risk Factors in {state.name}</h2>
                <div className="flex flex-wrap gap-2">
                  {state.risk_factors.map((r, i) => (
                    <span key={i} className="bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1 text-xs font-medium capitalize">{r}</span>
                  ))}
                </div>
              </div>
            )}

            {/* TOP COMPANIES */}
            {state.top_companies && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Top Auto Insurance Companies in {state.name}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {state.top_companies.map((c, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3 text-center text-xs font-medium text-slate-700">{c}</div>
                  ))}
                </div>
              </div>
            )}

            {/* COMPARE CTA */}
            <div className="bg-sky-700 rounded-xl p-6 text-center" data-testid="state-cta">
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Compare Auto Insurance in {state.name}</h3>
              <p className="text-sm text-sky-200 mb-3">See rates from top providers — free and no obligation.</p>
              <a href={getAffiliate(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-sky-700 rounded-xl px-6 py-3 text-sm font-semibold hover:bg-sky-50 transition-all mt-2" data-testid="state-compare-btn">
                Get Free Quotes <ArrowRight size={14} />
              </a>
            </div>

            <QuizTeaser />

            {/* MOBILE ADS */}
            <div className="lg:hidden">
              <AdSenseOnly />
            </div>

            {/* CITIES IN STATE */}
            <section data-testid="state-cities">
              <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Cities in {state.name}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {state.cities?.map(c => (
                  <Link key={c.slug} to={`/quotes/${c.slug}`} onClick={scrollToTop} className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:text-sky-700 hover:border-sky-300 transition-all flex items-center gap-1.5 font-medium" data-testid={`state-city-${c.slug}`}>
                    <MapPin size={12} className="text-slate-400 shrink-0" /> {c.city}
                  </Link>
                ))}
              </div>

              {/* CAN'T FIND YOUR CITY BANNER */}
              <div className="bg-slate-100 border border-slate-200 rounded-2xl p-6 sm:p-8 text-center mt-6" data-testid="cant-find-city-banner">
                <MapPin size={24} className="text-sky-700 mx-auto mb-2" />
                <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Can't Find Your City?</h3>
                <p className="text-sm text-slate-500 mb-4">Don't worry — our auto insurance guide covers every city in the US. Click below to search for yours.</p>
                <Link to="/find-your-city" onClick={scrollToTop} className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-xl px-6 py-3 text-sm font-medium hover:bg-sky-800 transition-all" data-testid="cant-find-city-link">
                  Auto Insurance Guide for All Cities <ArrowRight size={14} />
                </Link>
              </div>
            </section>
          </div>

          {/* SIDEBAR */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6 hidden lg:block" data-testid="sidebar">
            <div className="lg:sticky lg:top-20 space-y-6">
              <AdSensePlaceholder size="large" />
            </div>
          </aside>
        </div>
      </div>
      <Footer />
      <ChatBot />
      <CookieConsent />
    </div>
  );
}
